import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moduleHeroConductor from '@modules/hero-module/module-hero-conductor';
import moduleConductor from '../modules/module-conductor';
import Layout from '@components/layout';
import SEO from '@components/seo';
import IndustrySlash from '@components/industry/industrySlash';
import { media } from '@styles';

const StyledSlashSection = styled.div`
  position: relative;
  overflow: hidden;
  display: block;
  ${media.tablet`display: none;`};
`;

const StyledSlash = styled(IndustrySlash)`
  display: block;
  z-index: 10;
  ${media.desktop`left: 62%;`}
  ${media.tablet`display: none;`};
`;

const StyledMobileContainer = styled.div`
  display: none;
  ${media.tablet`display: block;`};
`;

const StyledMobileSection = styled.div`
  position: relative;
  overflow: hidden;
`;

const IndustryTemplate = ({ location, data }) => {
  const {
    pageData: { seo, hero, mission, results, footer, sections },
    insightQuery,
    featuredQuery,
    breadcrumbList
  } = data;

  const heroSection = hero && moduleHeroConductor(hero, hero.type, { slashType: 'mobile-only-slash' });
  const insights = insightQuery && insightQuery.nodes;
  const featured = featuredQuery && featuredQuery.nodes;
  const breadcrumbs = breadcrumbList && breadcrumbList.nodes;
  const missionModule = mission && moduleConductor(mission, 0, { contentGrid: 6 });
  const resultModule = results && moduleConductor(results, 1, { pageType: 'industry', contentGrid: 5 });

  const modulesMarkup = sections.map((module, i) => {
    let extraProps = {};
    if (module.__typename === 'ContentfulModuleGlobalItem') {
      if (module.type === 'featured') {
        extraProps = { data: (featured && featured[0]) || {} };
      } else if (module.type === 'insight-slide') {
        extraProps = { data: insights || [] };
      }
    }
    if (module.__typename === 'ContentfulModuleTwoColumnCards') {
      extraProps = { sectionType: 'services-list' };
    }
    return moduleConductor(module, i, extraProps);
  });

  return (
    <Layout location={location} footer={footer}>
      {seo && <SEO data={seo} location={location} breadcrumbs={breadcrumbs} />}
      <StyledSlashSection>
        {heroSection}
        {missionModule}
        {resultModule}
        <StyledSlash />
      </StyledSlashSection>
      <StyledMobileContainer>
        <StyledMobileSection>
          {heroSection}
          <StyledSlash />
        </StyledMobileSection>
        {missionModule}
        {resultModule}
      </StyledMobileContainer>
      {modulesMarkup}
    </Layout>
  );
};

IndustryTemplate.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default IndustryTemplate;

export const pageQuery = graphql`
  query($contentfulId: String!, $pageSlug: String!, $breadcrumbRegex: String!) {
    pageData: contentfulTemplateIndustry(contentful_id: { eq: $contentfulId }) {
      id
      contentful_id
      slug
      url
      seo {
        ...GlobalSEOFragment
      }
      hero {
        ...ModuleHeroFragment
      }
      mission {
        ...ModuleOneColumnFragment
      }
      results {
        ...ModuleResultsFragment
      }
      sections {
        ...ModuleOneColumnFragment
        ...ModuleTwoColumnCardsFragment
        ...ModuleServiceListFragment
        ...ModuleAdSectionFragment
        ...ModuleTopicsFragment
        ...ModuleResultsFragment
        ...ModuleAnimationSlideFragment
        ...ModuleMultiCardFragment
        ...ModuleHeaderFragment
        ...ModuleGlobalItemFragment
      }
      footer
    }
    insightQuery: allContentfulTemplateInsight(
      limit: 3
      sort: { fields: publishDate, order: DESC }
    ) {
      nodes {
        ...GlobalInsightFragment
      }
    }
    featuredQuery: allContentfulTemplateInsight(
      filter: { location: { eq: $pageSlug } }
      sort: { fields: publishDate, order: DESC }
    ) {
      nodes {
        ...GlobalInsightFragment
      }
    }
    breadcrumbList: allContentfulSeo(filter: { pageUrl: { regex: $breadcrumbRegex } }) {
      nodes {
        pageUrl
        pageTitle
      }
    }
  }
`;
